import p2 from 'p2';

export interface PhysicsContactDescription extends p2.ContactMaterialOptions
{
    name: string,
}

export class PhysicsContact
{
    public world: p2.World;
    public materials: Record<string, p2.Material> = {};
    public descriptions: Record<string, PhysicsContactDescription> = {};
    public contacts: Record<string, p2.ContactMaterial> = {};

    constructor(world: p2.World)
    {
        this.world = world;
    }

    public getMaterial(desc: PhysicsContactDescription): p2.Material
    {
        if (!this.materials[desc.name])
        {
            this.materials[desc.name] = new p2.Material();
            this.descriptions[desc.name] = desc;
            this.refresh();
        }

        return this.materials[desc.name];
    }

    public getDefaultGroundMaterial(): p2.Material
    {
        return this.getMaterial({
            name: `ground-default`,
            friction: 2.5,
        });
    }

    public refresh(): void
    {
        for (const nameA in this.descriptions)
        {
            const descA = this.descriptions[nameA];

            for (const nameB in this.descriptions)
            {
                const descB = this.descriptions[nameB];

                if (descA === descB) continue;

                const mixed = this.mixDescriptions(descA, descB);

                if (this.contacts[mixed.name]) continue;

                const contact = new p2.ContactMaterial(this.materials[nameA], this.materials[nameB], mixed);

                this.contacts[mixed.name] = contact;
                this.world.addContactMaterial(contact);
                console.log('[PhysicsContacts] Contact material added:', mixed);
                console.log('[PhysicsContacts] Num contacts:', Object.keys(this.contacts).length);
            }
        }

        if (Object.keys(this.contacts).length !== this.world.contactMaterials.length)
        {
            console.warn('[PhysicsContact] Contact mismatch! Something has gone wrong while generating physics contacts.');
        }
    }

    private getKey(...names: string[]): string
    {
        return names.sort().join('|');
    }

    private mixDescriptions(descA: PhysicsContactDescription, descB: PhysicsContactDescription): PhysicsContactDescription
    {
        const mixed: any = {
            name: this.getKey(descA.name, descB.name),
            restitution: this.mixValues(descA.restitution, descB.restitution),
            friction: this.mixValues(descA.friction, descB.friction),
            surfaceVelocity: this.mixValues(descA.surfaceVelocity, descB.surfaceVelocity),
            relaxation: this.maxValue(descA.relaxation, descB.relaxation),
            stiffness: this.maxValue(descA.stiffness, descB.stiffness),
            frictionStiffness: this.maxValue(descA.frictionStiffness, descB.frictionStiffness),
            frictionRelaxation: this.maxValue(descA.frictionRelaxation, descB.frictionRelaxation),
        };

        // Delete undefined keys
        Object.keys(mixed).forEach((key) => mixed[key] === undefined && delete mixed[key]);

        return mixed;
    }

    private mixValues(a?: number, b?: number): number | undefined
    {
        if (a !== undefined && b === undefined) return a;
        if (a === undefined && b !== undefined) return b;
        if (a !== undefined && b !== undefined) return (a + b) * 0.5;

        return undefined;
    }

    private maxValue(a?: number, b?: number): number | undefined
    {
        if (a !== undefined && b === undefined) return a;
        if (a === undefined && b !== undefined) return b;
        if (a !== undefined && b !== undefined) return Math.max(a, b);

        return undefined;
    }
}
